import type { FC, PropsWithChildren } from "react";
import { NATIONAL_FOOTBALL_TEAMS } from "src/shared/constants/SportsCategories";
import { useFallbackImageSrc } from "src/shared/hooks/useFallbackImageSrc";
import { fullWidth } from "src/website-apps/responsive/stubhub-performer/components/mobile-banner/styles";
import {
  COMPONENT_CSS_VAR_NAMES,
  styledSolidBackgroundOverlay,
} from "src/website-apps/responsive/stubhub-performer/components/solid-background-overlay/styles";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { Image, ImageSize } from "src/website-apps/shared/components/image";
import { useIsIsWorldCupCategory } from "src/website-apps/shared/hooks/useIsWorldCupCategory";
import {
  PerformerLabelType,
  useWillShowPerformerCredibilityLabel,
} from "src/website-apps/shared/hooks/useWillShowPerformerCredibilityLabel";
import { mobileBannerCardGlassBackgroundRecipe } from "./MobileBannerCardGlassBackground.styled";

interface Props {
  /** image to use as the bg for the glass image */
  imageUrl: string;
}

export const MobileBannerCardGlassBackground: FC<PropsWithChildren<Props>> = ({ children, imageUrl }) => {
  const fallbackImageSrc = useFallbackImageSrc();
  const model = usePerformerPageModelData();
  const { primaryColor, backgroundColor } = model.categoryMetadata?.sports?.partnerTheme ?? {};

  const isWorldCupCategory = useIsIsWorldCupCategory();
  const isWorldCupRelatedCategory =
    isWorldCupCategory ||
    (!!model.categorySummary.categoryId && NATIONAL_FOOTBALL_TEAMS.includes(model.categorySummary.categoryId));
  const showFooter = isWorldCupRelatedCategory;

  const labelType = useWillShowPerformerCredibilityLabel();
  const willMoveUpOfficialPartner = showFooter && labelType === PerformerLabelType.FAN_PROTECT_MOBILE;

  const { headerWrapper, container, image, imageOverlay } = mobileBannerCardGlassBackgroundRecipe({
    bottomRadius: showFooter && !willMoveUpOfficialPartner ? "hidden" : "visible",
  });

  return (
    <div className={fullWidth}>
      <div className={headerWrapper}>
        {children}
        <div className={container} aria-hidden>
          {primaryColor === undefined ? (
            <>
              <div className={imageOverlay} />
              <Image
                className={image}
                alt={model.categorySummary.categoryName}
                fallbackImageSrc={fallbackImageSrc}
                size={ImageSize.GlassImage}
                src={imageUrl}
              />
            </>
          ) : (
            /* partner overlay colours */
            <div
              className={styledSolidBackgroundOverlay}
              style={{
                [COMPONENT_CSS_VAR_NAMES.stripeColor]: primaryColor ?? undefined,
                [COMPONENT_CSS_VAR_NAMES.backgroundColor]: backgroundColor ?? undefined,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};
